import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, Typography, Box, Chip, Grid, CircularProgress } from '@mui/material';
import { CreditCard, AccountBalance, SwapHoriz, Payments } from '@mui/icons-material';
import { SCUIButton } from '../../../../../atomic/atoms';
import { RefundRestQuery } from '../../../../../services/query';
import Helper from '../../../../../utils/helper';
import Swal from 'sweetalert2';

interface PaymentConversionCardProps {
    refundId?: string;
    refundAmount?: number;
    currencySymbol?: string;
}

export const PaymentConversionCard: React.FC<PaymentConversionCardProps> = ({
    refundId,
    refundAmount,
    currencySymbol,
}) => {
    const { t } = useTranslation();
    const [isConverting, setIsConverting] = useState(false);

    const handleConvertPayment = async () => {
        const result = await Swal.fire({
            title: t('paymentConversion.confirmConversion'),
            text: t('paymentConversion.conversionWarning'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('paymentConversion.convert'),
            cancelButtonText: t('general.cancel'),
        });

        if (result.isConfirmed) {
            try {
                setIsConverting(true);
                const response = await RefundRestQuery.ConvertPaybackToBankTransfer({
                    refundId: Number(refundId),
                });
                if (response?.success) {
                    Swal.fire({
                        title: t('paymentConversion.conversionSuccess'),
                        text: t('paymentConversion.conversionSuccessMessage'),
                        icon: 'success',
                        timer: 1500,
                        timerProgressBar: true,
                        showConfirmButton: false,
                    }).then(() => {
                        window.location.reload();
                    });
                    return;
                } else {
                    throw new Error(response?.data?.message || 'Conversion failed');
                }
            } catch (error: any) {
                console.error('Payment conversion error:', error);
                Swal.fire({
                    title: t('general.error'),
                    text: error?.response?.data?.message || t('paymentConversion.conversionError'),
                    icon: 'error',
                });
            } finally {
                setIsConverting(false);
            }
        }
    };

    return (
        <Card sx={{ marginTop: '2rem' }}>
            <CardHeader
                title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SwapHoriz color="primary" />
                        <Typography variant="h6">{t('paymentConversion.title')}</Typography>
                    </Box>
                }
                subheader={t('paymentConversion.subtitle')}
                sx={{ pb: 1 }}
            />
            <CardContent sx={{ pt: 1, pb: 2 }}>
                <Grid container spacing={1}>
                    <Grid
                        size={{
                            xs: 12,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <CreditCard color="error" fontSize="small" />
                            <Typography variant="caption" color="text.secondary">
                                {t('paymentConversion.currentPayment')}
                            </Typography>
                            <Chip
                                label={t('paymentConversion.creditCard')}
                                color="error"
                                variant="outlined"
                                size="small"
                            />
                        </Box>
                    </Grid>

                    <Grid
                        size={{
                            xs: 12,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <AccountBalance color="success" fontSize="small" />
                            <Typography variant="caption" color="text.secondary">
                                {t('paymentConversion.targetPayment')}
                            </Typography>
                            <Chip
                                label={t('paymentConversion.bankTransfer')}
                                color="success"
                                variant="outlined"
                                size="small"
                            />
                        </Box>
                    </Grid>

                    <Grid
                        size={{
                            xs: 12,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Payments color="primary" fontSize="small" />
                            <Typography variant="caption" color="text.secondary">
                                {t('paymentConversion.amount')}
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">
                                {refundAmount != null
                                    ? `${Helper.formatNumber(refundAmount)} ${currencySymbol ?? ''}`.trim()
                                    : '-'}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid
                        size={{
                            xs: 12,
                        }}
                    >
                        <SCUIButton
                            type="primary"
                            sizeType="default"
                            size="m"
                            text={
                                isConverting ? t('paymentConversion.converting') : t('paymentConversion.convertButton')
                            }
                            onClick={handleConvertPayment}
                            disabled={isConverting}
                            showStartIcon={true}
                            startIcon={isConverting ? <CircularProgress size={16} /> : <SwapHoriz />}
                            fullwidth={true}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};
